import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, APP_INITIALIZER, NgModule } from '@angular/core';
import * as i1$2 from '@ngrx/store';
import { createAction, props, createReducer, on, StoreModule, createFeatureSelector, createSelector } from '@ngrx/store';
import * as i1$1 from '@ngrx/effects';
import { createEffect, ofType, EffectsModule } from '@ngrx/effects';
import { createEntityAdapter } from '@ngrx/entity';
import { throwError, of, combineLatest } from 'rxjs';
import { catchError, concatMap, map, filter } from 'rxjs/operators';
import * as i2 from '@panamax/app-state';
import { retryBackoff, defaultApiRetryStrategy } from '@panamax/app-state';
import * as i1 from '@angular/common/http';

const PROMOTIONS_KEY = 'promotions';

const PROMOTION_ACTIONS = {
    loadPromotions: createAction('[NGRXTrace - Promotions] Load Promotions', props()),
    loadPromotionsSuccess: createAction('[NGRXTrace - Promotions] Load Promotions Success', props()),
    loadPromotionsFail: createAction('[NGRXTrace - Promotions] Load Promotions Failed', props()),
    resetStore: createAction('[NgRxPromotion - Promotions] Reset Store'),
    updateDealsProductsExists: createAction('[NgRxPromotion - Promotions] Update dealsProductExists flag in store', props()),
};

/* eslint-disable no-shadow */
const promotionAdapter = createEntityAdapter({
    selectId: (promotion) => promotion.productNumber,
});
const initialPromotionState = promotionAdapter.getInitialState({
    loaded: false,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _PromotionReducer = createReducer(initialPromotionState, on(PROMOTION_ACTIONS.loadPromotions, (state, action) => ({
    ...state,
    loaded: false,
    loading: true,
})), on(PROMOTION_ACTIONS.loadPromotionsSuccess, (state, action) => promotionAdapter.upsertMany(action.promotionDetails, {
    ...state,
    loaded: true,
    loading: false,
})), on(PROMOTION_ACTIONS.loadPromotionsFail, (state, action) => ({
    ...state,
    error: action.error,
    loaded: true,
    loading: false,
})), on(PROMOTION_ACTIONS.updateDealsProductsExists, (state, action) => ({
    ...state,
    dealsProductsExist: action.newValue,
})), on(PROMOTION_ACTIONS.resetStore, (state, action) => initialPromotionState));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function promotionReducer(state, action) {
    return _PromotionReducer(state, action);
}

/**
 * Deals Load
 *
 * @param tracking
 * @returns Data Layer needed for tracking the deals load
 */
const dealsLoadTransformer = (tracking) => ({
    event: tracking.data.attributes.event,
});

const NGRX_PROMOTION_LIB_CONFIG = new InjectionToken('NGRX_PROMOTION_LIB_CONFIG');

/* eslint-disable no-shadow */
class PromotionHttpClientService {
    constructor(config, http) {
        this.config = config;
        this.http = http;
    }
    get(path, queryParams) {
        let url = `${this.config.apiUrl}/${path}`;
        if (queryParams) {
            queryParams.forEach((val, key) => {
                url += url.includes('?') ? `&${key}=${val}` : `?${key}=${val}`;
            });
        }
        return this.http
            .get(url)
            .pipe(retryBackoff(defaultApiRetryStrategy), catchError(this.handleError));
    }
    handleError(error) {
        return throwError(error);
    }
}
PromotionHttpClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PromotionHttpClientService, deps: [{ token: NGRX_PROMOTION_LIB_CONFIG }, { token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
PromotionHttpClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PromotionHttpClientService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PromotionHttpClientService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PROMOTION_LIB_CONFIG]
                }] }, { type: i1.HttpClient }]; } });

class PromotionEffects {
    constructor(actions$, httpService) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.load$ = createEffect(() => this.actions$.pipe(ofType(PROMOTION_ACTIONS.loadPromotions), concatMap((action) => {
            const params = new Map();
            params.set('requestUsage', 'D');
            return this.httpService.get('promotion', params).pipe(map((promotion) => {
                const promotionDetails = this.filterPromotionDetails(promotion);
                return PROMOTION_ACTIONS.loadPromotionsSuccess({
                    promotionDetails,
                    tracking: {
                        tracing: {
                            data: {
                                traceContext: 'deals',
                                isEndOfTrace: true,
                                isStartOfTrace: false,
                                attributes: {
                                    event: 'Deals API Load Success',
                                },
                            },
                            transformFunc: dealsLoadTransformer,
                        },
                    },
                });
            }), catchError((error) => of(PROMOTION_ACTIONS.loadPromotionsFail({
                error,
                tracking: {
                    tracing: {
                        data: {
                            traceContext: 'deals',
                            isEndOfTrace: true,
                            isStartOfTrace: false,
                            attributes: {
                                event: 'Deals API Load Failure',
                            },
                        },
                        transformFunc: dealsLoadTransformer,
                    },
                },
            }))));
        })));
    }
    filterPromotionDetails(promotion) {
        const promotionDetailsToReturn = [];
        const promotionDetailsToIterate = promotion.promotions;
        const dealCodePriorities = this.setCodeMap();
        const today = new Date();
        const productPriorities = new Map();
        promotionDetailsToIterate.forEach((promo) => {
            const promoStartDate = new Date(promo.offerStartDate);
            const promoEndDate = new Date(promo.offerEndDate);
            if (today >= promoStartDate && today <= promoEndDate) {
                const promoInMap = productPriorities.get(promo.productNumber);
                if (promoInMap) {
                    const promoInMapPriority = dealCodePriorities.get(promoInMap.offerAllowanceCode);
                    const promoPriority = dealCodePriorities.get(promo.offerAllowanceCode);
                    if (promoPriority > promoInMapPriority) {
                        productPriorities.set(promo.productNumber, promo);
                    }
                }
                else {
                    productPriorities.set(promo.productNumber, promo);
                }
            }
        });
        productPriorities.forEach((promotionDetails) => {
            promotionDetailsToReturn.push(promotionDetails);
        });
        return promotionDetailsToReturn;
    }
    setCodeMap() {
        const offerCodesMap = new Map();
        // 5 is highest priority
        offerCodesMap.set('475', 5);
        offerCodesMap.set('079', 4);
        offerCodesMap.set('645', 3);
        offerCodesMap.set('124', 2);
        offerCodesMap.set('657', 1);
        return offerCodesMap;
    }
}
PromotionEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PromotionEffects, deps: [{ token: i1$1.Actions }, { token: PromotionHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
PromotionEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PromotionEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PromotionEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$1.Actions }, { type: PromotionHttpClientService }]; } });

class PromotionService {
    constructor(store, panAppState) {
        this.store = store;
        this.panAppState = panAppState;
    }
    initialize() {
        combineLatest([
            this.panAppState.customer$,
            this.panAppState.feature$(['split_global_deals']),
            this.panAppState.user$,
        ])
            .pipe(filter(([customer, flag, user]) => customer?.selected && user.userId !== 'GUESTUSER'))
            .subscribe(([customer, flag]) => {
            this.store.dispatch(PROMOTION_ACTIONS.resetStore());
            if (flag) {
                this.store.dispatch(PROMOTION_ACTIONS.loadPromotions({
                    tracking: {
                        tracing: {
                            data: {
                                traceContext: 'deals',
                                isEndOfTrace: false,
                                isStartOfTrace: true,
                                attributes: {
                                    event: 'Deals API Load ',
                                },
                            },
                            transformFunc: dealsLoadTransformer,
                        },
                    },
                }));
            }
        });
    }
}
PromotionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PromotionService, deps: [{ token: i1$2.Store }, { token: i2.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
PromotionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PromotionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PromotionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Store }, { type: i2.PanAppState }]; } });

/* eslint-disable prefer-arrow/prefer-arrow-functions */
class NgRxPromotionsModule {
    constructor(ngrxPromotionLibConfig, store, panAppState) {
        this.ngrxPromotionLibConfig = ngrxPromotionLibConfig;
        this.store = store;
        this.panAppState = panAppState;
    }
    static forRoot(ngRxPromotionLibConfig, store, panAppState) {
        return {
            ngModule: NgRxPromotionsModule,
            providers: [
                {
                    provide: NGRX_PROMOTION_LIB_CONFIG,
                    useValue: ngRxPromotionLibConfig,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializePromotionState,
                    deps: [PromotionService],
                    multi: true,
                },
            ],
        };
    }
}
NgRxPromotionsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgRxPromotionsModule, deps: [{ token: NGRX_PROMOTION_LIB_CONFIG }, { token: i1$2.Store }, { token: i2.PanAppState }], target: i0.ɵɵFactoryTarget.NgModule });
NgRxPromotionsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: NgRxPromotionsModule, imports: [i1$2.StoreFeatureModule, i1$1.EffectsFeatureModule] });
NgRxPromotionsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgRxPromotionsModule, providers: [PromotionHttpClientService, PromotionEffects, PromotionService], imports: [StoreModule.forFeature(PROMOTIONS_KEY, promotionReducer),
        EffectsModule.forFeature([PromotionEffects])] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgRxPromotionsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        StoreModule.forFeature(PROMOTIONS_KEY, promotionReducer),
                        EffectsModule.forFeature([PromotionEffects]),
                    ],
                    providers: [PromotionHttpClientService, PromotionEffects, PromotionService],
                    exports: [],
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PROMOTION_LIB_CONFIG]
                }] }, { type: i1$2.Store }, { type: i2.PanAppState }]; } });
function initializePromotionState(promotionState) {
    const state = () => promotionState.initialize();
    return state;
}

const DEALS_LANDING_ANALYTICS_ACTIONS = {
    trackPageLoad: createAction('[NgRxClient Analytics] - Track Deals Landing Page Load', props()),
};

// eslint-disable-next-line no-shadow
var DealsPageAnylticsMessage;
(function (DealsPageAnylticsMessage) {
    DealsPageAnylticsMessage["capability"] = "product discovery";
    DealsPageAnylticsMessage["pageName"] = "us foods:r4:deals";
    DealsPageAnylticsMessage["pageLoadedEvent"] = "page loaded";
})(DealsPageAnylticsMessage || (DealsPageAnylticsMessage = {}));

const defaultTransformerFunc = (tracking) => ({
    ...tracking.data,
});

class DealsAnalyticsService {
    constructor(store) {
        this.store = store;
    }
    trackPageLoad() {
        const tracking = {
            analytics: {
                data: {
                    page: {
                        pageName: DealsPageAnylticsMessage.pageName,
                    },
                    event: DealsPageAnylticsMessage.pageLoadedEvent,
                    capability: DealsPageAnylticsMessage.capability,
                },
                transformFunc: defaultTransformerFunc,
            },
        };
        this.store.dispatch(DEALS_LANDING_ANALYTICS_ACTIONS.trackPageLoad({ tracking }));
    }
}
DealsAnalyticsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DealsAnalyticsService, deps: [{ token: i1$2.Store }], target: i0.ɵɵFactoryTarget.Injectable });
DealsAnalyticsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DealsAnalyticsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: DealsAnalyticsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Store }]; } });

const selectPromotionState = createFeatureSelector(PROMOTIONS_KEY);
const getAllPromotions = () => createSelector(selectPromotionState, (promotions) => Object.values(promotions.entities));
const getDealsProductExistsFlag = createSelector(selectPromotionState, (promotionState) => promotionState.dealsProductsExist);
const { selectAll: selectAll } = promotionAdapter.getSelectors(selectPromotionState);

/*
 * Public API Surface of ngrx-promotions
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DEALS_LANDING_ANALYTICS_ACTIONS, DealsAnalyticsService, NgRxPromotionsModule, PROMOTIONS_KEY, PROMOTION_ACTIONS, PromotionService, getAllPromotions, getDealsProductExistsFlag, initializePromotionState, promotionAdapter, promotionReducer, selectPromotionState };
